import { V4UUID } from '@/firebase/database';
import {
  toCamelCase,
  toSnakeCase
} from '@/modules/globalModule/components/helperMethods';
import {
  Message,
  MessageField
} from '@/modules/nodesCanvasModule/components/storeClasses/message';
import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';
import JSZip from 'jszip';
import { getMsgDependency } from './dependenciesGetter';

/**
 * generates a string representing a message field. for use in generating
 * msg and srv files
 * @param field - field to transform into text
 * @param workspaceId - id of workspace
 * @param pkgId - id of package that the interface file is being generated in.
 * Determines if the string will include a package dependency or not
 */
export const generateFieldString = (
  field: MessageField,
  workspaceId: V4UUID,
  pkgId: V4UUID
): string => {
  const messageInfo = getMsgDependency(workspaceId, field.msg);
  if (!messageInfo) return '';
  const message = messageInfo.msg;
  const msgPackageText: string =
    !message.stdMsg && pkgId !== messageInfo.packageId
      ? `${messageInfo.packageName}/`
      : '';
  const msgBoundsText = field.typeBounds ? `<=${field.typeBounds}` : '';
  // text representing the fields array value, if it exists
  const msgArrayText =
    (field.isArray &&
      field.arrayBounds &&
      field.arrayBoundsIsLength &&
      `[${field.arrayBounds}]`) ||
    (field.isArray && field.arrayBounds && `[<=${field.arrayBounds}]`) ||
    (field.isArray && `[]`) ||
    '';
  // text represesenting the default or constant value of the field
  const defaultConstText =
    (field.defaultValue && ` ${field.defaultValue}`) ||
    (field.constantValue && `=${field.constantValue}`) ||
    '';

  let fieldName = toSnakeCase(field.name);
  if (field.constantValue) fieldName = fieldName.toUpperCase();

  return `${msgPackageText}${toSnakeCase(
    message.name
  )} ${fieldName}${msgBoundsText}${msgArrayText}${defaultConstText}\n`;
};

/**
 * Generates all of the message files of a certian package. Creates a new msg folder
 * within the current JSZip folder, stores the message files in there.
 * @param zip - JSZip of the packages msg folder
 * @param workspaceId - id of workspace currently being used
 * @param pkg - package to generate the messages of
 * @returns
 */
export const generateMessageFiles = (
  zip: JSZip,
  workspaceId: V4UUID,
  pkg: PackageT
): void => {
  const msgFolder: JSZip = zip.folder('msg')!;
  for (let message of pkg.msgs) {
    if (message.stdMsg) continue;
    msgFolder.file(
      `${toCamelCase(message.name)}.msg`,
      generateMessageText(pkg.id, workspaceId, message)
    );
  }
};

export const generateMessageText = (
  pkgId: V4UUID,
  workspaceId: V4UUID,
  msg: Message
): string => {
  let messageText = '';
  for (let field of msg.fields) {
    messageText += generateFieldString(field, workspaceId, pkgId);
  }
  messageText += '# add custom message fields below';
  messageText += msg.customText;
  return messageText;
};
